import React, { useCallback, useEffect, useState } from "react";
import { LifeBuoy, UserPlus, Trash2, Clock, ShieldCheck, AlertTriangle, Send, KeyRound, Eye, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=120&h=120&fit=crop",
  "https://images.pexels.com/photos/12311572/pexels-photo-12311572.jpeg?auto=compress&cs=tinysrgb&w=120&h=120&fit=crop",
  "https://images.pexels.com/photos/30161439/pexels-photo-30161439.jpeg?auto=compress&cs=tinysrgb&w=120&h=120&fit=crop",
];

function avatarFor(seed) {
  if (!seed) return AVATARS[0];
  let s = 0;
  for (const c of seed) s = (s + c.charCodeAt(0)) % 9973;
  return AVATARS[s % AVATARS.length];
}

export default function EmergencyAccess() {
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [grantedToMe, setGrantedToMe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [vaultItems, setVaultItems] = useState([]);
  const [vaultOwner, setVaultOwner] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, g] = await Promise.all([
        api.get("/emergency/contacts"),
        api.get("/emergency/requests"),
        api.get("/emergency/granted-to-me"),
      ]);
      setContacts(c.data);
      setRequests(r.data);
      setGrantedToMe(g.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeContact = async (id) => {
    try {
      await api.delete(`/emergency/contacts/${id}`);
      toast.success("Trusted contact removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/emergency/requests/${id}/approve`);
      toast.success("Access granted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const deny = async (id) => {
    try {
      await api.post(`/emergency/requests/${id}/deny`);
      toast.success("Access denied");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const requestAccess = async (contactId) => {
    try {
      await api.post(`/emergency/request/${contactId}`);
      toast.success("Request sent. Owner will be notified.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed");
    }
  };

  const openOwnerVault = async (ownerId, ownerEmail) => {
    try {
      const { data } = await api.get(`/emergency/vault/${ownerId}`);
      setVaultItems(data.items || []);
      setVaultOwner(ownerEmail);
      setVaultOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Access not yet available");
    }
  };

  return (
    <div className="space-y-7">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.08em] text-[color:var(--vault-text-tertiary)]">Emergency access</p>
          <h1 className="font-[Outfit] text-4xl tracking-tight font-semibold mt-1">If something happens</h1>
          <p className="text-[color:var(--vault-text-secondary)] mt-2 max-w-2xl">
            Add trusted contacts who can request full access to your vault. After the waiting period passes (and if you don&apos;t deny it), they get read-only access automatically.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
          data-testid="add-trusted-contact-btn"
        >
          <UserPlus size={18} className="mr-1.5" /> Add trusted contact
        </Button>
      </div>

      {/* Trusted contacts */}
      <section>
        <h2 className="font-[Outfit] text-xl font-medium mb-3 flex items-center gap-2">
          <ShieldCheck size={18} /> My trusted contacts
        </h2>
        {loading ? (
          <div className="text-[color:var(--vault-text-tertiary)]">Loading…</div>
        ) : contacts.length === 0 ? (
          <EmptyBlock icon={LifeBuoy} title="No trusted contacts yet" subtitle="Add someone who could need access if something happens to you." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contacts-list">
            {contacts.map((c) => (
              <div key={c.id} className="vault-card p-5 flex gap-4" data-testid={`contact-card-${c.id}`}>
                <img src={avatarFor(c.contact_email)} alt="" className="w-12 h-12 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-[Outfit] font-medium truncate">{c.contact_name || c.contact_email}</div>
                  <div className="text-xs text-[color:var(--vault-text-tertiary)] truncate">{c.contact_email}</div>
                  <div className="text-xs mt-2 text-[color:var(--vault-text-secondary)] flex items-center gap-1">
                    <Clock size={12} /> {c.wait_days}-day wait period
                  </div>
                </div>
                <button
                  onClick={() => removeContact(c.id)}
                  className="p-2 self-start text-[color:var(--vault-error)] hover:bg-[color:var(--vault-error-bg)] rounded-lg"
                  title="Remove"
                  data-testid={`remove-contact-${c.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Incoming requests */}
      <section>
        <h2 className="font-[Outfit] text-xl font-medium mb-3 flex items-center gap-2">
          <AlertTriangle size={18} /> Requests for access to MY vault
        </h2>
        {requests.length === 0 ? (
          <div className="text-sm text-[color:var(--vault-text-tertiary)]">No pending requests.</div>
        ) : (
          <ul className="space-y-3" data-testid="incoming-requests">
            {requests.map((r) => {
              const statusColor = r.status === "approved" ? "var(--vault-success)"
                : r.status === "denied" ? "var(--vault-error)" : "var(--vault-warning)";
              const statusBg = r.status === "approved" ? "var(--vault-success_bg)"
                : r.status === "denied" ? "var(--vault-error-bg)" : "var(--vault-warning_bg)";
              return (
                <li key={r.id} className="vault-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`request-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <img src={avatarFor(r.contact_email)} alt="" className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="font-medium">{r.contact_name || r.contact_email}</div>
                      <div className="text-xs text-[color:var(--vault-text-tertiary)]">
                        Requested {formatDate(r.requested_at)} · auto-grants {formatDate(r.expires_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-medium px-2.5 py-1 rounded-full capitalize"
                      style={{ background: statusBg, color: statusColor }}
                    >
                      {r.status}
                    </span>
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => deny(r.id)} data-testid={`deny-${r.id}`}>
                          Deny
                        </Button>
                        <Button size="sm" className="rounded-lg bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" onClick={() => approve(r.id)} data-testid={`approve-${r.id}`}>
                          Approve now
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Vaults I might access */}
      <section>
        <h2 className="font-[Outfit] text-xl font-medium mb-3 flex items-center gap-2">
          <KeyRound size={18} /> Vaults you can access in an emergency
        </h2>
        {grantedToMe.length === 0 ? (
          <div className="text-sm text-[color:var(--vault-text-tertiary)]">You haven&apos;t been designated as a trusted contact yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="granted-to-me-list">
            {grantedToMe.map((g) => {
              const req = g.request;
              const canAccess = req && (req.status === "approved" || (req.status === "pending" && new Date(req.expires_at) <= new Date()));
              return (
                <div key={g.id} className="vault-card p-5" data-testid={`granted-${g.id}`}>
                  <div className="flex items-center gap-3">
                    <img src={avatarFor(g.owner_email)} alt="" className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-[Outfit] font-medium truncate">{g.owner_name || g.owner_email}</div>
                      <div className="text-xs text-[color:var(--vault-text-tertiary)] truncate">{g.owner_email}</div>
                      <div className="text-xs mt-1 text-[color:var(--vault-text-secondary)] flex items-center gap-1">
                        <Clock size={12} /> {g.wait_days}-day wait
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    {!req ? (
                      <Button size="sm" onClick={() => requestAccess(g.id)} className="rounded-lg bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid={`request-access-${g.id}`}>
                        <Send size={14} className="mr-1.5" /> Request access
                      </Button>
                    ) : req.status === "denied" ? (
                      <div className="text-xs" style={{ color: "var(--vault-error)" }}>Request denied</div>
                    ) : canAccess ? (
                      <Button size="sm" onClick={() => openOwnerVault(g.owner_id, g.owner_email)} className="rounded-lg bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid={`view-vault-${g.id}`}>
                        <Eye size={14} className="mr-1.5" /> View vault
                      </Button>
                    ) : (
                      <div className="text-xs" style={{ color: "var(--vault-warning)" }}>
                        Waiting — auto-grants {formatDate(req.expires_at)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <AddContactDialog open={addOpen} onOpenChange={setAddOpen} onAdded={load} />

      <Dialog open={vaultOpen} onOpenChange={setVaultOpen}>
        <DialogContent className="bg-white rounded-2xl max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] text-xl">Emergency vault access — {vaultOwner}</DialogTitle>
          </DialogHeader>
          {vaultItems.length === 0 ? (
            <div className="text-sm text-[color:var(--vault-text-tertiary)] py-6">This vault is empty.</div>
          ) : (
            <ul className="space-y-3 mt-2">
              {vaultItems.map((it) => (
                <li key={it.id} className="rounded-xl border border-[color:var(--vault-border)] p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-[Outfit] font-medium">{it.title}</div>
                      <div className="text-xs text-[color:var(--vault-text-tertiary)]">
                        {it.username} {it.url ? `· ${it.url}` : ""}
                      </div>
                    </div>
                    <span className="pill">{it.category}</span>
                  </div>
                  <div className="mt-2 font-mono text-sm bg-[#F1F5FA] border border-[color:var(--vault-border)] rounded-lg px-3 py-2 flex items-center justify-between">
                    <span className="truncate">{it.password || "—"}</span>
                    <button
                      onClick={async () => { await navigator.clipboard.writeText(it.password || ""); toast.success("Copied"); }}
                      className="p-1 text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)]"
                      data-testid={`emergency-copy-${it.id}`}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                  {it.notes && (
                    <div className="mt-2 text-sm text-[color:var(--vault-text-secondary)] whitespace-pre-wrap font-mono">{it.notes}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVaultOpen(false)} className="rounded-xl">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyBlock({ icon: Icon, title, subtitle }) {
  return (
    <div className="vault-card p-8 text-center">
      <div className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center text-white" style={{ background: "var(--vault-secondary)" }}>
        <Icon size={22} />
      </div>
      <h3 className="font-[Outfit] text-lg mt-3 font-medium">{title}</h3>
      <p className="text-sm text-[color:var(--vault-text-secondary)] mt-1">{subtitle}</p>
    </div>
  );
}

function AddContactDialog({ open, onOpenChange, onAdded }) {
  const [email, setEmail] = useState("");
  const [waitDays, setWaitDays] = useState(3);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setWaitDays(3); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/emergency/contacts", { contact_email: email.trim(), wait_days: Number(waitDays) });
      toast.success("Trusted contact added");
      onAdded();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl">Add a trusted contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Their email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="wife@example.com"
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
              data-testid="contact-email-input"
            />
            <p className="text-xs text-[color:var(--vault-text-tertiary)] mt-1">
              They need an account on EZGuard. Ask them to sign up first if they haven&apos;t.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Waiting period (days)</Label>
            <Input
              type="number"
              min={0}
              max={90}
              value={waitDays}
              onChange={(e) => setWaitDays(e.target.value)}
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
              data-testid="contact-wait-days-input"
            />
            <p className="text-xs text-[color:var(--vault-text-tertiary)] mt-1">
              If they request access, you have this many days to deny before they get read-only access.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="contact-cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
              data-testid="contact-submit-btn"
            >
              {submitting ? "Adding…" : "Add contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}
